import React, { useState, useEffect } from "react";
import { api } from "../../lib/apiClient";
import { Plus, Save, Trash2 } from "lucide-react";
import { toast } from "../../components/ui/sonner";
import { RuleChangeHistory } from "./RuleChangeHistory";

export function ExtraCostsTab() {
  const [rules, setRules] = useState(null);
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState(null);

  useEffect(() => {
    api.get("/extra-cost-rules").then((r) => setRules(r.data));
  }, []);

  const setTier = (key, idx, patch) => {
    const next = { ...rules };
    next[key] = [...next[key]];
    next[key][idx] = { ...next[key][idx], ...patch };
    setRules(next);
  };
  const addTier = (key) => {
    const next = { ...rules };
    next[key] = [...next[key], { max_amount: 0, percent: 0 }];
    setRules(next);
  };
  const removeTier = (key, idx) => {
    const next = { ...rules };
    next[key] = next[key].filter((_, i) => i !== idx);
    setRules(next);
  };
  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        contingency_sundry_tiers: rules.contingency_sundry_tiers.map((t) => ({
          max_amount: t.max_amount === "" || t.max_amount == null ? null : Number(t.max_amount),
          percent: Number(t.percent),
        })),
        administration_tiers: rules.administration_tiers.map((t) => ({
          max_amount: t.max_amount === "" || t.max_amount == null ? null : Number(t.max_amount),
          percent: Number(t.percent),
        })),
        admin_on_labour_only: rules.admin_on_labour_only !== false,
      };
      const { data } = await api.put("/extra-cost-rules", payload);
      setRules(data);
      setSavedAt(new Date());
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save");
    } finally {
      setSaving(false);
    }
  };

  if (!rules) return <div className="text-sm text-slate-500">Loading…</div>;

  const TierTable = ({ title, blurb, kind, baseLabel }) => (
    <div className="border border-slate-200 p-4 space-y-3">
      <div>
        <h3 className="font-display text-lg font-black tracking-tight">{title}</h3>
        <p className="text-xs text-slate-500 mt-1 max-w-2xl">{blurb}</p>
      </div>
      <table className="w-full text-sm">
        <thead>
          <tr className="bg-slate-50 border-b-2 border-slate-900">
            <th className="text-left px-3 py-2 text-[10px] uppercase tracking-wider font-semibold text-slate-600">If {baseLabel} is ≤</th>
            <th className="text-left px-3 py-2 text-[10px] uppercase tracking-wider font-semibold text-slate-600">Apply %</th>
            <th />
          </tr>
        </thead>
        <tbody>
          {rules[kind].map((t, idx) => {
            const isCatchAll = t.max_amount == null;
            return (
              <tr key={idx} className="border-b border-slate-200">
                <td className="px-3 py-2">
                  {isCatchAll ? (
                    <span className="font-mono text-slate-500">Anything above</span>
                  ) : (
                    <input type="number" step="0.01"
                      value={t.max_amount ?? ""}
                      onChange={(e) => setTier(kind, idx, { max_amount: e.target.value })}
                      className="qb-input h-9 font-mono max-w-[200px]"
                      data-testid={`${kind}-max-${idx}`}
                    />
                  )}
                </td>
                <td className="px-3 py-2">
                  <input type="number" step="0.01"
                    value={t.percent}
                    onChange={(e) => setTier(kind, idx, { percent: e.target.value })}
                    className="qb-input h-9 font-mono max-w-[120px]"
                    data-testid={`${kind}-percent-${idx}`}
                  />
                </td>
                <td className="px-3 py-2 text-right">
                  <button onClick={() => removeTier(kind, idx)} className="text-slate-400 hover:text-red-600">
                    <Trash2 size={14} />
                  </button>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      <button
        onClick={() => addTier(kind)}
        className="text-xs uppercase tracking-wider font-semibold inline-flex items-center gap-1 text-slate-600 hover:text-slate-900"
        data-testid={`add-tier-${kind}`}
      >
        <Plus size={14} /> Add tier
      </button>
    </div>
  );

  return (
    <div className="space-y-6" data-testid="extra-costs-tab">
      <div className="text-xs text-slate-500 max-w-2xl">
        These two charges are <strong>auto-applied</strong> to every quote and shown under <em>One-off extras</em> in the Live Calculation.
        Estimators cannot edit them on a quote — only admins can change the tier rules here.
        Leave the last tier's <em>≤</em> field blank to make it the "anything above" catch-all.
      </div>
      <button
        type="button"
        onClick={() => {
          const uk = () => [{ max_amount: 1000, percent: 5 }, { max_amount: 5000, percent: 2.5 }, { max_amount: null, percent: 1 }];
          setRules({ ...rules, contingency_sundry_tiers: uk(), administration_tiers: uk() });
          toast.success("UK tiers filled in — click Save Rules to apply.");
        }}
        className="h-9 px-4 rounded-full border border-slate-300 text-xs uppercase tracking-wider font-semibold hover:border-slate-900 transition-colors"
        data-testid="reset-uk-tiers-btn"
      >
        Reset both to UK defaults (5% / 2.5% / 1%)
      </button>

      <TierTable
        title="Contingency Sundry"
        blurb="Percentage applied to Parts & Materials only (with mark-up), so Labour Only quotes get £0. UK default: ≤£1,000 → 5%, ≤£5,000 → 2.5%, anything above → 1%."
        kind="contingency_sundry_tiers"
        baseLabel="materials total"
      />

      <TierTable
        title="Administration Charge"
        blurb="Percentage applied to the total job value, including everything except callouts. UK default: ≤£1,000 → 5%, ≤£5,000 → 2.5%, anything above → 1%."
        kind="administration_tiers"
        baseLabel="job value (excl. callouts)"
      />

      <label className="flex items-center justify-between gap-3 p-4 border border-slate-200 bg-white max-w-3xl cursor-pointer" data-testid="admin-labour-only-toggle-wrap">
        <span>
          <span className="block text-sm font-semibold text-slate-900">Apply Administration Charge on Labour Only quotes</span>
          <span className="block text-xs text-slate-500">When off, quotes ticked "Labour Only" get no admin charge. Remember to Save Rules.</span>
        </span>
        <input type="checkbox" className="h-5 w-5 accent-slate-900" checked={rules.admin_on_labour_only !== false}
          onChange={(e) => setRules({ ...rules, admin_on_labour_only: e.target.checked })} data-testid="admin-labour-only-toggle" />
      </label>

      <div className="flex items-center gap-3">
        <button
          onClick={save}
          disabled={saving}
          className="h-9 px-4 bg-brand-blue text-white text-xs uppercase tracking-wider font-semibold inline-flex items-center gap-2 hover:bg-brand-blue-dark disabled:opacity-60"
          data-testid="save-extra-costs-button"
        >
          <Save size={14} /> {saving ? "Saving…" : "Save Rules"}
        </button>
        {savedAt && <span className="text-xs text-slate-500">Saved · {savedAt.toLocaleTimeString()}</span>}
      </div>
      <RuleChangeHistory refreshKey={savedAt?.getTime()} />
    </div>
  );
}
